import React from 'react';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { NodeAnchors, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeError } from '@common/hooks/useNodeError';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

import {
  StoreNodeState as StoreMessageNodeState,
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeOutput,
  NodeType,
  NodeInputType,
} from '..';

export const config: NodeConfig = {
  version: '1.0',
  title: 'Store Message',
  identifier: 'StoreMessage',
  command_id: 'database.store_message',
  icon: 'store',
  background: '#3434EF',
  anchors: [
    {
      label: 'Execute',
      id: 'database.store_message.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'database.store_message.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      label: 'Stored',
      id: 'database.store_message.stored',
      type: 'source' as HandleType,
      variant: 'execution',
    },
  ],
};

export const state: StoreMessageNodeState = {
  session_id: {
    type: NodeInputType.Expression,
    value: '',
  },
  user_id: {
    type: NodeInputType.Expression,
    value: '',
  },
  user_message: {
    type: NodeInputType.Expression,
    value: '',
  },
  bot_message: {
    type: NodeInputType.Expression,
    value: '',
  },
};

export const fields: NodeField[] = [
  {
    id: 'session_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Session ID',
    description:
      "The sessionID is used to represent the conversation history. You can store different conversation histories by using different sessionID's. You can retrieve this conversation history in the AI Completion node using the sessionID field and entering the same identifier.",
    placeholder: '1234-1234-1234-1234',
  },
  {
    id: 'user_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'User ID',
    description: 'This field can be used to store a user ID to your conversation history.',
    placeholder: '1234-1234-1234-1234',
  },
  {
    id: 'user_message',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'User Message',
    description:
      'This field can be used to store a query from the user to your conversation history. Use the sessionID field to specify which conversation to store the message to.',
    placeholder: 'A query from the user',
  },
  {
    id: 'bot_message',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Bot Response',
    description:
      'This field can be used to store a response from the LLM to your conversation history. Use the sessionID field to specify which conversation to store the message to.',
    placeholder: `A response from the LLM`,
  },
];

export const output: NodeOutput = { default: null, dynamic: null };

export const StoreMessage: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'database.store_message') return null;

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
